import { useState, useEffect } from 'react';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { Table, Title, Loader } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import '@mantine/dates/styles.css';
import { IoCreateOutline, IoCheckmark, IoClose } from 'react-icons/io5';
import { QuietAddTrigger, QuietIconButton, Input, RefetchButton, Switch } from '@shared/ui';
import './EditableDataTable.css';

export interface EditableDataTableColumn<T = any> {
  key: string;
  label: string;
  render?: (item: T, isEditing: boolean, value: any, onChange: (value: any) => void) => ReactNode;
  type?: 'text' | 'number' | 'switch' | 'time' | 'date';
  editable?: boolean;
  style?: React.CSSProperties;
  getValue?: (item: T) => any;
  setValue?: (item: T, value: any) => void;
}

export interface EditableDataTableEditExtrasContext<T extends { id: number | string }> {
  editableData: T[];
  setEditableData: Dispatch<SetStateAction<T[]>>;
}

export interface EditableDataTableProps<T extends { id: number | string }> {
  title: string;
  description?: ReactNode;
  columns: EditableDataTableColumn<T>[];
  data: T[];
  onSave: (updatedData: T[]) => Promise<void> | void;
  isLoading?: boolean;
  emptyMessage?: string;
  rowEditMode?: boolean; // Режим редактирования отдельных строк
  getNewRow?: () => T; // Функция для создания новой строки (для rowEditMode)
  error?: unknown;
  refetch?: () => void;
  isDataLoading?: boolean;
  /** Текст кнопки добавления (rowEditMode) */
  addLabel?: string;
  /** Текст кнопки редактирования */
  editLabel?: string;
  /** Доп. контент под заголовком в режиме редактирования всей таблицы */
  editExtras?: (ctx: EditableDataTableEditExtrasContext<T>) => ReactNode;
  /** Кастомная логика обновления строк при редактировании всей таблицы */
  transformEditableUpdate?: (args: {
    prev: T[];
    itemId: number | string;
    columnKey: string;
    value: any;
    nextItem: T;
  }) => T[];
}

export default function EditableDataTable<T extends { id: number | string }>({
  title,
  description,
  columns,
  data,
  onSave,
  isLoading = false,
  emptyMessage = 'Нет данных',
  rowEditMode = false,
  getNewRow,
  error,
  refetch,
  isDataLoading,
  addLabel = 'Добавить',
  editLabel = 'Редактировать',
  editExtras,
  transformEditableUpdate,
}: EditableDataTableProps<T>) {
  const [isEditing, setIsEditing] = useState(false);
  const [editableData, setEditableData] = useState<T[]>([]);
  const [editingRowId, setEditingRowId] = useState<number | string | null>(null);
  const [editingRowData, setEditingRowData] = useState<T | null>(null);

  useEffect(() => {
    if (!isEditing && !rowEditMode) {
      setEditableData(data.map(item => ({ ...item })));
    } else if (rowEditMode) {
      setEditableData(data.map(item => ({ ...item })));
    }
  }, [data, isEditing, rowEditMode]);

  const handleEdit = () => {
    setEditableData(data.map(item => ({ ...item })));
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      await onSave(editableData);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving data:', error);
    }
  };

  const handleCancel = () => {
    setEditableData(data.map(item => ({ ...item })));
    setIsEditing(false);
  };

  const handleRowEdit = (item: T) => {
    setEditingRowId(item.id);
    setEditingRowData({ ...item });
  };

  const handleRowSave = async (itemId: number | string) => {
    if (!editingRowData) return;

    const updatedData = editableData.map(item =>
      item.id === itemId ? editingRowData : item
    );

    try {
      await onSave(updatedData);
      setEditingRowId(null);
      setEditingRowData(null);
    } catch (error) {
      console.error('Error saving row:', error);
    }
  };

  const handleRowCancel = () => {
    if (editingRowId && String(editingRowId).startsWith('new-')) {
      setEditableData(prev => prev.filter(item => item.id !== editingRowId));
    }
    setEditingRowId(null);
    setEditingRowData(null);
  };

  const handleAddRow = () => {
    if (!getNewRow) return;
    const newRow = getNewRow();
    const newId = `new-${Date.now()}`;
    const rowWithId = { ...newRow, id: newId };
    setEditableData([...editableData, rowWithId]);
    setEditingRowId(newId);
    setEditingRowData(rowWithId);
  };

  const handleDeleteRow = async (itemId: number | string) => {
    const updatedData = editableData.filter(item => item.id !== itemId);
    try {
      await onSave(updatedData);
    } catch (error) {
      console.error('Error deleting row:', error);
    }
  };

  const updateItemValue = (itemId: number | string, columnKey: string, value: any) => {
    if (rowEditMode && editingRowId === itemId && editingRowData) {
      if (columns.find(col => col.key === columnKey)?.setValue) {
        const column = columns.find(col => col.key === columnKey)!;
        const newItem = { ...editingRowData };
        column.setValue!(newItem, value);
        setEditingRowData(newItem);
      } else {
        setEditingRowData({ ...editingRowData, [columnKey]: value });
      }
    } else {
      setEditableData(prev => {
        const current = prev.find(item => item.id === itemId);
        if (!current) return prev;

        let nextItem: T;
        if (columns.find(col => col.key === columnKey)?.setValue) {
          const column = columns.find(col => col.key === columnKey)!;
          nextItem = { ...current };
          column.setValue!(nextItem, value);
        } else {
          nextItem = { ...current, [columnKey]: value };
        }

        if (transformEditableUpdate) {
          return transformEditableUpdate({
            prev,
            itemId,
            columnKey,
            value,
            nextItem,
          });
        }

        return prev.map(item => (item.id === itemId ? nextItem : item));
      });
    }
  };

  const renderCell = (column: EditableDataTableColumn<T>, item: T, value: any, onChange: (value: any) => void) => {
    const isRowEditing = rowEditMode && editingRowId === item.id;
    const isCellEditable = rowEditMode ? isRowEditing : isEditing;

    if (column.render) {
      return column.render(item, isCellEditable, value, onChange);
    }

    if (!isCellEditable || !column.editable) {
      if (column.type === 'switch') {
        return <span>{value ? 'Да' : 'Нет'}</span>;
      }
      if (column.type === 'date') {
        if (!value) return <span>-</span>;
        if (value instanceof Date) {
          const dd = String(value.getDate()).padStart(2, '0');
          const mm = String(value.getMonth() + 1).padStart(2, '0');
          const yyyy = value.getFullYear();
          return <span>{`${dd}.${mm}.${yyyy}`}</span>;
        }
        return <span>{value}</span>;
      }
      return <span>{value ?? '-'}</span>;
    }

    switch (column.type) {
      case 'number':
        return (
          <Input
            type="number"
            value={value ?? 0}
            onChange={(e) => onChange(e.currentTarget.value ? Number(e.currentTarget.value) : 0)}
            size="xs"
            className="editable-data-table__input editable-data-table__input--number"
          />
        );
      case 'switch':
        return (
          <Switch
            checked={value ?? false}
            onChange={(e) => onChange(e.currentTarget.checked)}
            size="xs"
          />
        );
      case 'time':
        return (
          <Input
            type="time"
            value={value ?? ''}
            onChange={(e) => onChange(e.currentTarget.value)}
            size="xs"
            className="editable-data-table__input editable-data-table__input--time"
          />
        );
      case 'date': {
        let dateValue: Date | null = null;
        if (value) {
          if (value instanceof Date) {
            dateValue = value;
          } else if (typeof value === 'string' && value.trim() !== '') {
            dateValue = new Date(value);
            if (isNaN(dateValue.getTime())) {
              dateValue = null;
            }
          }
        }
        return (
          <DateInput
            value={dateValue}
            onChange={(value) => onChange(value)}
            size="xs"
            valueFormat="DD.MM.YYYY"
            placeholder="ДД.ММ.ГГГГ"
            className="editable-data-table__input editable-data-table__input--date"
            clearable
          />
        );
      }
      default:
        return (
          <Input
            value={value ?? ''}
            onChange={(e) => onChange(e.currentTarget.value)}
            size="xs"
            className="editable-data-table__input editable-data-table__input--text"
          />
        );
    }
  };

  const displayData = rowEditMode
    ? editableData
    : (isEditing ? editableData : data);

  const totalCols = columns.length + (rowEditMode ? 1 : 0);
  const equalColWidth = `${100 / totalCols}%`;
  const colStyle = (column: EditableDataTableColumn<T>) => ({
    width: equalColWidth,
    ...column.style,
  });

  const renderHead = (actions: ReactNode) => (
    <>
      <div className="editable-data-table__head">
        <Title order={4} className="editable-data-table__title">
          {title}
        </Title>
        {actions}
      </div>
      {description ? (
        <p className="editable-data-table__description">{description}</p>
      ) : null}
      {isEditing && editExtras ? (
        <div className="editable-data-table__edit-extras">
          {editExtras({ editableData, setEditableData })}
        </div>
      ) : null}
    </>
  );

  if (isDataLoading) {
    return (
      <div className="editable-data-table">
        {renderHead(null)}
        <div className="empty-container">
          <Loader />
        </div>
      </div>
    );
  }

  if (error && refetch) {
    return (
      <div className="editable-data-table">
        {renderHead(null)}
        <div className="empty-container">
          <RefetchButton
            refetch={refetch}
            isLoading={isDataLoading}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="editable-data-table">
      {renderHead(
        rowEditMode ? (
          <div className="editable-data-table__actions">
            <QuietAddTrigger
              onClick={handleAddRow}
              disabled={!!editingRowId}
              aria-label={addLabel}
            >
              {addLabel}
            </QuietAddTrigger>
          </div>
        ) : !isEditing ? (
          <div className="editable-data-table__actions">
            <QuietIconButton
              onClick={handleEdit}
              icon={<IoCreateOutline size={16} />}
              aria-label={editLabel}
            >
              {editLabel}
            </QuietIconButton>
          </div>
        ) : (
          <div className="editable-data-table__actions">
            <QuietIconButton
              icon={<IoCheckmark size={18} />}
              tone="accent"
              onClick={handleSave}
              loading={isLoading}
              aria-label="Сохранить"
            />
            <QuietIconButton
              icon={<IoClose size={18} />}
              tone="danger"
              onClick={handleCancel}
              disabled={isLoading}
              aria-label="Отменить"
            />
          </div>
        ),
      )}
      <Table
        className="editable-data-table__table"
        highlightOnHover
        withRowBorders={false}
        horizontalSpacing="md"
        verticalSpacing="sm"
      >
        <Table.Thead>
          <Table.Tr>
            {columns.map((column) => (
              <Table.Th key={column.key} style={colStyle(column)}>
                {column.label}
              </Table.Th>
            ))}
            {rowEditMode && <Table.Th style={{ width: equalColWidth }}> </Table.Th>}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {displayData.length > 0 ? (
            displayData.map((item) => {
              const isRowEditing = rowEditMode && editingRowId === item.id;
              const displayItem = isRowEditing && editingRowData ? editingRowData : item;

              return (
                <Table.Tr key={item.id}>
                  {columns.map((column) => {
                    const value = column.getValue
                      ? column.getValue(displayItem)
                      : (displayItem as any)[column.key];
                    const onChange = (newValue: any) => {
                      updateItemValue(item.id, column.key, newValue);
                    };
                    return (
                      <Table.Td key={column.key} style={colStyle(column)}>
                        {renderCell(column, displayItem, value, onChange)}
                      </Table.Td>
                    );
                  })}
                  {rowEditMode && (
                    <Table.Td style={{ width: equalColWidth }}>
                      {isRowEditing ? (
                        <div className="editable-data-table__row-actions">
                          <button
                            type="button"
                            className="editable-data-table__text-action editable-data-table__text-action--ok"
                            onClick={() => handleRowSave(item.id)}
                            disabled={isLoading}
                          >
                            Сохранить
                          </button>
                          <button
                            type="button"
                            className="editable-data-table__text-action"
                            onClick={handleRowCancel}
                            disabled={isLoading}
                          >
                            Отмена
                          </button>
                        </div>
                      ) : (
                        <div className="editable-data-table__row-actions">
                          <button
                            type="button"
                            className="editable-data-table__text-action"
                            onClick={() => handleRowEdit(item)}
                            disabled={!!editingRowId}
                          >
                            Изменить
                          </button>
                          <button
                            type="button"
                            className="editable-data-table__text-action editable-data-table__text-action--danger"
                            onClick={() => handleDeleteRow(item.id)}
                            disabled={!!editingRowId || isLoading}
                          >
                            Удалить
                          </button>
                        </div>
                      )}
                    </Table.Td>
                  )}
                </Table.Tr>
              );
            })
          ) : (
            <Table.Tr>
              <Table.Td
                colSpan={columns.length + (rowEditMode ? 1 : 0)}
                className="editable-data-table__empty"
              >
                {emptyMessage}
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    </div>
  );
}
